import { ARTICLES, getArticleBySlug, type Article } from '../../content/articles';
import { HOME_FAQ } from '../../pages/home/home-faq';
import { environment } from '../../../environments/environment';
import type { SeoMeta } from './seo.types';

export const SEO_DEFAULT_IMAGE = 'assets/ali-representative-photo.png';
export const SEO_SITE_NAME = 'Skilled';

const ORIGIN = environment.publicSiteUrl.replace(/\/$/, '');

export function absoluteUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${ORIGIN}${normalized}`;
}

export function absoluteAssetUrl(assetPath: string): string {
  const normalized = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
  return `${ORIGIN}${normalized}`;
}

function faqJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: HOME_FAQ.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

function organizationJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SEO_SITE_NAME,
    url: ORIGIN,
    logo: absoluteAssetUrl('assets/brand/skilled-logo-mark.svg'),
    sameAs: [
      'https://www.linkedin.com/company/aiskilled',
      'https://x.com/weaisklled',
      'https://www.instagram.com/skilled.sales/',
    ],
  };
}

function websiteJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SEO_SITE_NAME,
    url: ORIGIN,
  };
}

function softwareApplicationJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Ali by Skilled',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description:
      'Agentic AI sales rep for B2B outbound: prospecting, personalized outreach, follow-ups, and meeting booking.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free trial available',
    },
    provider: {
      '@type': 'Organization',
      name: SEO_SITE_NAME,
      url: ORIGIN,
    },
  };
}

function articleJsonLd(article: Article): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.excerpt,
    image: absoluteAssetUrl(article.image),
    author: {
      '@type': 'Person',
      name: article.author.name,
    },
    publisher: {
      '@type': 'Organization',
      name: SEO_SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: absoluteAssetUrl('assets/brand/skilled-logo-mark.svg'),
      },
    },
    datePublished: article.date,
    mainEntityOfPage: absoluteUrl(`/content-hub/${article.slug}`),
  };
}

export function buildArticleSeo(slug: string): SeoMeta | null {
  const article = getArticleBySlug(slug);
  if (!article) {
    return null;
  }
  return {
    title: `${article.title} | ${SEO_SITE_NAME}`,
    description: article.excerpt,
    path: `/content-hub/${article.slug}`,
    image: article.image,
    type: 'article',
    jsonLd: [articleJsonLd(article), organizationJsonLd()],
  };
}

export const SEO_BY_KEY: Record<string, SeoMeta> = {
  home: {
    title: 'Skilled: Ali, the AI Sales Rep Built for the Gulf',
    description:
      'Ali finds your next buyers, writes to them in Arabic and English, and books the meeting. Verified GCC leads, PDPL-aware outreach, your CRM in sync.',
    path: '/',
    jsonLd: [organizationJsonLd(), websiteJsonLd(), faqJsonLd()],
  },
  meetAli: {
    title: 'Meet Ali: Build Your First Campaign by Talking to Him | Skilled',
    description:
      "Ali, Skilled's AI sales rep, asks a few questions about who you sell to, out loud, and builds your first outbound campaign from your answers.",
    path: '/meet-ali',
    jsonLd: [organizationJsonLd()],
  },
  aliSalesReps: {
    title: 'Ali: Agentic AI SDR for B2B Outbound | Skilled',
    description:
      'Meet Ali: an agentic AI sales rep for prospecting, cold outreach, intelligent follow-ups, meeting booking, and CRM sync for B2B teams.',
    path: '/products/ali-sales-reps',
    jsonLd: [softwareApplicationJsonLd(), organizationJsonLd()],
  },
  skilledCard: {
    title: 'Skilled Networking: Digital Business Card & Event Lead Capture',
    description:
      'Share a live digital business card by QR or link. Capture badge and paper leads at events into CRM. Built for B2B client-facing teams — business email only.',
    path: '/products/skilled-networking',
    image: 'assets/screenshots/skilled-card/card.png',
    jsonLd: [organizationJsonLd()],
  },
  compare: {
    title: 'Ali vs Global AI SDR Tools: Gulf Outbound Comparison | Skilled',
    description:
      'Compare Ali to global AI SDR tools and DIY data-plus-sequencer stacks. Verified GCC data, Arabic-native outreach, and PDPL-aware sending.',
    path: '/compare',
    jsonLd: [organizationJsonLd()],
  },
  pricing: {
    title: 'Pricing: Outbound Plans for B2B Teams | Skilled',
    description:
      'Choose a Skilled plan to launch faster, reach better prospects, and turn outbound into predictable pipeline growth.',
    path: '/pricing',
    jsonLd: [organizationJsonLd()],
  },
  company: {
    title: 'About Skilled: Team, Mission & Agentic AI for B2B Sales',
    description:
      'Meet the team building practical agentic AI for B2B sales. Skilled’s mission, leadership, events, and advisory board.',
    path: '/company',
    jsonLd: [organizationJsonLd(), faqJsonLd()],
  },
  contentHub: {
    title: 'Gulf Outbound & AI SDR Resources | Skilled',
    description:
      'Playbooks on Gulf outbound, Saudi PDPL, Arabic + English outreach, and AI SDRs from the team building Ali.',
    path: '/content-hub',
    jsonLd: [organizationJsonLd()],
  },
  referral: {
    title: 'Referral Program: Share Skilled, Earn Credits',
    description:
      'Invite another B2B team to Skilled. When they activate a paid plan, you earn 100 credits and they earn 50.',
    path: '/referral',
    jsonLd: [organizationJsonLd()],
  },
  privacyPolicy: {
    title: 'Privacy Policy | Skilled',
    description: 'How Skilled collects, uses, and protects personal information on our marketing site and products.',
    path: '/privacy-policy',
  },
  termsOfUse: {
    title: 'Terms of Use | Skilled',
    description: 'Terms and conditions for using Skilled’s marketing site and AI sales services.',
    path: '/terms-of-use',
  },
  dontUseMyInformation: {
    title: 'Do Not Use My Information | Skilled',
    description: 'Request that Skilled not use your personal information for marketing or sales outreach.',
    path: '/dont-use-my-information',
  },
  chatWithAli: {
    title: 'Chat with Ali: Try Skilled’s AI Sales Partner',
    description: 'Start a conversation with Ali to see how agentic AI can run outbound for your B2B team.',
    path: '/chat-with-ali',
    robots: 'noindex, nofollow',
  },
  paymentCheckout: {
    title: 'Checkout | Skilled',
    description: 'Complete your Skilled plan purchase.',
    path: '/payment-checkout',
    robots: 'noindex, nofollow',
  },
  campaignPreview: {
    title: 'Campaign Preview | Skilled',
    description: 'Preview your Skilled campaign customer profile.',
    path: '/campaign-preview/customer-profile',
    robots: 'noindex, nofollow',
  },
};

/** All indexable paths for sitemap generation. */
export function indexablePaths(): string[] {
  const staticPaths = Object.values(SEO_BY_KEY)
    .filter((meta) => !meta.robots?.includes('noindex'))
    .map((meta) => meta.path);
  const articlePaths = ARTICLES.map((a) => `/content-hub/${a.slug}`);
  return [...staticPaths, ...articlePaths];
}
